import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { OwlDateTimeIntl } from 'ng-pick-datetime-ex';
import { TranslateService } from '@ngx-translate/core';
import { AppLocalStorage } from 'src/app/utils/app-local-storage-utils';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { BranchService } from 'src/app/services/branch.service';
import { SocketService } from '../services/socket.service';
import { WEB_SOCKET_EVENTS } from '../enums/web-socket-events';
import { MediaComponent } from '../media/media.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-create-task',
  templateUrl: './create-task.component.html',
  styleUrls: ['./create-task.component.scss']
})
export class CreateTaskComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedTaskType: string = '';
  selectedPurpose: string = '';
  selectedSeverity: string = '';
  selectedUser: string = '';
  selectedBranchId: string = '';

  // Date picker properties
  plannedStartTime: Date;
  plannedEndTime: Date;
  currentDate = new Date();
  isHour12Format: boolean;
  public placeholder: any;
  timeZone: string;

  // input for title and description
  taskTitle: string = '';
  taskDescription: string = '';

  // Status message properties
  statusMessage: string = '';
  statusType: 'success' | 'error' = 'success';

  // Table properties
  showTable: boolean = true;
  tasksDataSource: any[] = [];
  filteredTasksDataSource: any[] = [];
  selectedTaskId: string = '';
  displayedColumns: string[] = [
    'title',
    'description',
    'taskType',
    'purpose',
    'branch',
    'assignedTo',
    'severity',
    'plannedStartTime',
    'plannedEndTime',
    'status',
    'actions'
  ];

  // Action display properties
  showApprovalForm: boolean = false;
  showLogViewer: boolean = false;
  selectedTaskForAction: any = null;
  approvalComment: string = '';
  taskLogs: any[] = [];

  taskTypes = [
    {
      value: 'maintenance',
      name: 'Maintenance',
      purposes: [
        { value: 'preventive', name: 'Preventive' },
        { value: 'corrective', name: 'Corrective' }
      ]
    },
    // {
    //   value: 'inspection',
    //   name: 'Inspection',
    //   purposes: []
    // },
    // {
    //   value: 'installation',
    //   name: 'Installation',
    //   purposes: []
    // }
  ];

  severities = [
    { value: 'critical', name: 'Critical' },
    { value: 'high', name: 'High' },
    { value: 'medium', name: 'Medium' },
    { value: 'low', name: 'Low' }
  ];

  users = [];
  branchesWithGuards: any[] = [];
  @ViewChild('rootContainer', { static: false }) rootContainer: ElementRef;
  searchText: string = '';

  statusOptions = [
    { title: 'Pending', value: 'pending' },
    { title: 'In Progress', value: 'in_progress' },
    { title: 'Completed', value: 'completed' },
    { title: 'Rejected', value: 'rejected' }
  ];

  constructor(
    public translate: TranslateService,
    public owlDateTimeIntl: OwlDateTimeIntl,
    private authenticationService: AuthenticationService,
    private branchService: BranchService,
    private socketService: SocketService,
    public dialog: MatDialog
  ) { }

  async ngOnInit() {
    this.initializeDatePicker();
    await this.getGuards();
    await this.getTasks();
    this.webSocketInit();
  }

  async getGuards() {
    const response = await this.branchService.getBranchesGuards();

    // Flatten all guards from all branches into a single array with branch info
    this.users = response.flatMap((branch: any) =>
      branch.guards.map((guard: any) => ({
        value: guard._id,
        name: `${guard.firstName} ${guard.lastName}`,
        branchName: branch.branchName,
        branchId: branch.branchId
      }))
    );

    // Group users by branch name
    const grouped = this.users.reduce((acc: any, user: any) => {
      if (!acc[user.branchName]) {
        acc[user.branchName] = {
          branchName: user.branchName,
          guards: []
        };
      }
      acc[user.branchName].guards.push({
        value: user.value,
        name: user.name,
        branchId: user.branchId
      });
      return acc;
    }, {});

    this.branchesWithGuards = Object.values(grouped);
  }

  initializeDatePicker() {
    this.timeZone = this.authenticationService.getUserTimezone();

    // Set default start time to current time in user's timezone
    this.plannedStartTime = new Date(Date.now());
    // Set default end time to current time + 2 hours in user's timezone
    this.plannedEndTime = new Date(Date.now() + 2 * 60 * 60 * 1000);

    // Initialize date picker internationalization
    this.owlDateTimeIntl.setBtnLabel = this.translate.instant('ANALYTICAL_FILTERS.SAVE_TEXT');
    this.owlDateTimeIntl.cancelBtnLabel = this.translate.instant('ANALYTICAL_FILTERS.CANCEL_TEXT');
    this.owlDateTimeIntl.hour12AMLabel = this.translate.instant('FILTER_DATA.AM');
    this.owlDateTimeIntl.hour12PMLabel = this.translate.instant('FILTER_DATA.PM');

    this.placeholder = {
      selectDateAndTime: this.translate.instant('ANALYTICAL_FILTERS.SELECT_DATE_AND_TIME'),
    };

    // Set hour format based on language
    let lang = AppLocalStorage.getLanguage();
    this.isHour12Format = (lang !== "ru");
  }

  onTaskTypeChange(value: string): void {
    this.selectedTaskType = value;
    this.selectedPurpose = '';
  }

  getCurrentTaskTypePurposes(): any[] {
    const taskType = this.taskTypes.find(type => type.value === this.selectedTaskType);
    return taskType?.purposes || [];
  }

  onPurposeChange(value: string): void {
    this.selectedPurpose = value;
  }

  onSeverityChange(value: string): void {
    this.selectedSeverity = value;
  }

  onUserChange(value: string): void {
    this.selectedUser = value;
    // Find the selected guard and get their branch ID
    const selectedGuard = this.users.find(user => user.value === value);
    this.selectedBranchId = selectedGuard ? selectedGuard.branchId : '';
  }

  onStartTimeChange(event: any): void {
    this.plannedStartTime = event;
  }

  onEndTimeChange(event: any): void {
    this.plannedEndTime = event;
  }

  onTitleChange(value: string): void {
    this.taskTitle = value;
  }

  onDescriptionChange(value: string): void {
    this.taskDescription = value;
  }

  async createTask() {
    this.statusMessage = '';

    const task = {
      title: this.taskTitle,
      description: this.taskDescription,
      taskType: this.selectedTaskType,
      taskTypeDetails: {
        maintenanceType: this.selectedPurpose,
        branch: this.selectedBranchId
      },
      severity: this.selectedSeverity,
      assignedTo: this.selectedUser,
      timing: {
        plannedStartTime: this.plannedStartTime.toISOString(),
        plannedEndTime: this.plannedEndTime.toISOString()
      }
    }

    const response = await this.branchService.createTask(task);
    if (response?.success) {
      this.showStatusMessage(this.translate.instant('TASK.TASK_CREATED_SUCCESSFULLY'), 'success');
      this.resetForm();
      await this.getTasks(); // Refresh the tasks list
    } else {
      let message = response?.errorMessages[0].message || this.translate.instant('TASK.TASK_CREATION_FAILED');
      this.showStatusMessage(message, 'error');
    }
  }

  resetForm() {
    // Reset all form fields
    this.selectedTaskType = '';
    this.selectedPurpose = '';
    this.selectedSeverity = '';
    this.selectedUser = '';
    this.selectedBranchId = '';
    this.taskTitle = '';
    this.taskDescription = '';

    // Reset date pickers to default values
    this.plannedStartTime = new Date(Date.now());
    this.plannedEndTime = new Date(Date.now() + 2 * 60 * 60 * 1000);
  }

  showStatusMessage(message: string, type: 'success' | 'error') {
    this.statusMessage = message;
    this.statusType = type;

    // Auto-hide messages after 5 seconds
    setTimeout(() => {
      this.statusMessage = '';
    }, 5000);
  }

  async getTasks() {
    const response = await this.branchService.getTasks();
    this.tasksDataSource = response || [];
    this.applySearchFilter();
  }

  applySearchFilter() {
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      this.filteredTasksDataSource = this.tasksDataSource;
    } else {
      this.filteredTasksDataSource = this.tasksDataSource.filter(task => {
        return (
          (task.title || '').toLowerCase().includes(search) ||
          (task.description || '').toLowerCase().includes(search) ||
          (task.taskType || '').toLowerCase().includes(search) ||
          (this.getTaskPurpose(task) || '').toLowerCase().includes(search) ||
          (this.getTaskBranch(task) || '').toLowerCase().includes(search) ||
          (this.getTaskAssignedTo(task) || '').toLowerCase().includes(search) ||
          (task.severity || '').toLowerCase().includes(search) ||
          (task.status || '').toLowerCase().includes(search)
        );
      });
    }
  }

  onSearchInputChange() {
    this.applySearchFilter();
  }

  getGuardName(guardId: string): string {
    const guard = this.users.find(user => user.value === guardId);
    return guard ? guard.name : 'Unknown';
  }

  getTaskBranch(task: any): string {
    return task.taskTypeDetails?.branch?.branchName || '-';
  }

  getTaskPurpose(task: any): string {
    return task.taskTypeDetails?.maintenanceType || '-';
  }

  getTaskAssignedTo(task: any): string {
    if (task.assignedTo?.firstName) {
      return `${task.assignedTo.firstName} ${task.assignedTo.lastName}`;
    }
    return '-';
  }

  hasAssetUsages(log: any): boolean {
    return log.assetUsages &&
      typeof log.assetUsages === 'object' &&
      log.assetUsages !== null &&
      Object.keys(log.assetUsages).length > 0;
  }

  getAttachments(attachmentUrl: any[]): string[] {
    if (!attachmentUrl || attachmentUrl.length === 0) {
      return [];
    }

    let attachments: string[] = [];

    try {
      // Handle the case where attachmentUrl contains JSON strings
      attachmentUrl.forEach((item: any) => {
        if (typeof item === 'string') {
          try {
            // Try to parse as JSON first
            const parsed = JSON.parse(item);
            if (Array.isArray(parsed)) {
              attachments.push(...parsed);
            } else {
              attachments.push(item);
            }
          } catch {
            // If not JSON, treat as direct URL
            attachments.push(item);
          }
        } else if (Array.isArray(item)) {
          attachments.push(...item);
        } else {
          attachments.push(item);
        }
      });
    } catch (error) {
      console.error('Error parsing attachments:', error);
    }

    return attachments.filter(attachment => attachment && attachment.trim() !== '');
  }

  getAttachmentInfo(attachment: string): any {
    const url = attachment.toString().toLowerCase();

    if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif') || url.includes('.bmp') || url.includes('.webp')) {
      return {
        icon: 'image',
        text: "User uploaded an image"
      }
    } else if (url.includes('.mp4') || url.includes('.avi') || url.includes('.mov') || url.includes('.wmv') || url.includes('.flv') || url.includes('.webm')) {
      return {
        icon: 'video_library',
        text: "User uploaded avideo"
      }
    } else if (url.includes('.mp3') || url.includes('.wav') || url.includes('.ogg') || url.includes('.aac') || url.includes('.flac') || url.includes('.m4a')) {
      return {
        icon: 'audio_file',
        text: "User uploaded an audio"
      }
    } else {
      return {
        icon: 'attach_file',
        text: "User uploaded a file"
      }
    }
  }

  isAudioFile(attachment: string): boolean {
    const url = attachment.toString().toLowerCase();
    return url.includes('.mp3') || url.includes('.wav') || url.includes('.ogg') || url.includes('.aac') || url.includes('.flac') || url.includes('.m4a');
  }

  highlightTask(task: any) {
    this.selectedTaskId = task._id;
  }

  openContextMenu(event: Event, task: any) {
    event.stopPropagation();
    this.selectedTaskForAction = task;
  }

  reviewTask(task: any) {
    this.selectedTaskForAction = task;
    this.showApprovalForm = true;
    this.showLogViewer = false;
  }

  async readTaskLog(task: any) {
    this.selectedTaskForAction = task;
    this.showLogViewer = true;
    this.showApprovalForm = false;
    await this.loadTaskLogs();
  }

  backToTable() {
    this.showTable = true;
    this.showApprovalForm = false;
    this.showLogViewer = false;
    this.selectedTaskForAction = null;
  }

  async approve() {
    const response = await this.branchService.approveTask(this.selectedTaskForAction._id, this.approvalComment);
    if (response?.success) {
      this.approvalComment = '';
      this.showStatusMessage(this.translate.instant('TASK.TASK_APPROVED_SUCCESSFULLY'), 'success');
      // Wait 5 seconds before going back to table
      setTimeout(async () => {
        this.backToTable();
        await this.getTasks();
      }, 5000);
    } else {
      let message = response?.errorMessages[0].message;
      this.showStatusMessage(message, 'error');
    }
  }

  async reject() {
    const response = await this.branchService.rejectTask(this.selectedTaskForAction._id, this.approvalComment);
    if (response?.success) {
      this.approvalComment = '';
      this.showStatusMessage(this.translate.instant('TASK.TASK_REJECTED'), 'success');
      // Wait 5 seconds before going back to table
      setTimeout(async () => {
        this.backToTable();
        await this.getTasks();
      }, 5000);
    } else {
      let message = response?.errorMessages[0].message;
      this.showStatusMessage(message, 'error');
    }
  }

  async loadTaskLogs() {
    this.taskLogs = await this.branchService.readTaskLog(this.selectedTaskForAction._id);
  }

  ngOnDestroy() {
  }

  updateDisplayedColumns(width: number) {
    if (width >= 1200) {
      this.displayedColumns = [
        'title', 'description', 'taskType', 'purpose', 'branch', 'assignedTo', 'severity', 'plannedStartTime', 'plannedEndTime', 'status', 'actions'
      ];
    } else if (width >= 900) {
      this.displayedColumns = [
        'title', 'description', 'taskType', 'purpose', 'branch', 'assignedTo', 'severity', 'status', 'actions'
      ];
    } else if (width >= 700) {
      this.displayedColumns = [
        'title', 'taskType', 'purpose', 'branch', 'assignedTo', 'status', 'actions'
      ];
    } else {
      this.displayedColumns = [
        'title', 'taskType', 'assignedTo', 'status', 'actions'
      ];
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.rootContainer?.nativeElement) {
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(entries => {
            for (let entry of entries) {
              this.updateDisplayedColumns(entry.contentRect.width);
            }
          });
          ro.observe(this.rootContainer.nativeElement);
        }
      } else {
        this.updateDisplayedColumns(window.innerWidth);
      }
    }, 100);
  }

  webSocketInit() {
    const socketId = localStorage.getItem('socketId');
    if (!socketId) {
      return;
    }
    const eventNotification = this.socketService.onEvent<any>(socketId);
    if (eventNotification) {
      eventNotification.subscribe((socketEvent) => {
        switch (socketEvent.type) {
          case WEB_SOCKET_EVENTS.NEW_TASK:
            this.getTasks();
            break;
          case WEB_SOCKET_EVENTS.UPDATE_TASK:
            if (this.showLogViewer && (this?.selectedTaskForAction?._id === socketEvent?.data)) {
              this.loadTaskLogs();
            }
            this.getTasks();
            break;
          default:
        }
      });
    }
  }

  displayMedia(element: any) {
    // Determine media type based on file extension
    let mediaType = 'photo'; // default
    const url = element.toString().toLowerCase();

    if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif') || url.includes('.bmp') || url.includes('.webp')) {
      mediaType = 'photo';
    } else if (url.includes('.mp4') || url.includes('.avi') || url.includes('.mov') || url.includes('.wmv') || url.includes('.flv') || url.includes('.webm')) {
      mediaType = 'video';
    }

    let media = {
      type: mediaType,
      url: element
    }

    this.dialog.open(MediaComponent, {
      height: '65%',
      width: '50%',
      disableClose: true,
      panelClass: 'custom-dialog',
      data: media
    });
  }
}
